import React, { ComponentPropsWithoutRef, MouseEventHandler, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Analytics from '@hh.ru/analytics-js';
import { ComponentWithCustomElement } from '@hh.ru/magritte-types';
import { Link, LinkProps } from '@hh.ru/magritte-ui';

import { Params } from './types';
import useMakePushData, { PushKnownState } from './useMakePushData';

const MAIN_MOUSE_BUTTON = 0;

// tempexp_31848_start
const Features = {
    enableAlternativeHHTMParams: 'enable_alternative_hhtm_params',
};
// tempexp_31848_end
const isModifiedEvent = (event: React.MouseEvent) => event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

type AsSpaLink = Omit<React.ComponentPropsWithoutRef<'a'>, 'href' | 'style'>;
type AsExternalButtonLink = Omit<LinkProps, 'href' | 'Element' | 'type' | 'style'>;
interface AsNonMagritteExternalButtonLink {
    nonMagritteExternalLink?: boolean;
}

interface SPALinkProps
    extends AsSpaLink,
        AsExternalButtonLink,
        AsNonMagritteExternalButtonLink,
        PushKnownState,
        Params {
    /** Контент */
    children?: React.ReactNode;
    /** Отключает обработку */
    disabled?: boolean;
    /** Дополнительный стейт для `router.location.state` */
    state?: Record<string, unknown>;
    to: string;
}

const defaultOnClick: MouseEventHandler = () => {};

const SPALink = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, SPALinkProps>(
    (
        {
            to,
            additionalQueryParams,
            children,
            onClick = defaultOnClick,
            target,
            cancelScrollCorrection,
            isSeoLink,
            forceHhtmForExternalLink,
            forceHhtmForInternalLink,
            cancelFetchingData,
            nonMagritteExternalLink,
            state,
            ...elementProps
        },
        ref
    ) => {
        const dispatch = useDispatch();
        // tempexp_31848_start
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const isAlternativeHHTMParamsEnabled = useSelector(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
            ({ features }) => features[Features.enableAlternativeHHTMParams]
        );
        const xsrfToken = useSelector(({ xsrfToken }) => xsrfToken);
        // tempexp_31848_end
        const makePushData = useMakePushData('Link');
        const { url, pushAction, useRoutingBySPA, useExternalLink, hhtm } = makePushData(
            to,
            {
                additionalQueryParams,
                cancelScrollCorrection,
                isSeoLink,
                forceHhtmForExternalLink,
                forceHhtmForInternalLink,
                ...(state || {}),
            },
            { cancelFetchingData }
        );

        const internalOnClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
            (event) => {
                onClick && onClick(event);
                if (!event.defaultPrevented && event.button === MAIN_MOUSE_BUTTON && !isModifiedEvent(event)) {
                    event.preventDefault();
                    dispatch(pushAction);
                    // tempexp_31848_start
                } else if (isAlternativeHHTMParamsEnabled) {
                    Analytics.sendHHEventToProxyButtonClick?.(xsrfToken, 'start_transition', {
                        hhtmFrom: hhtm.from,
                        hhtmFromLabel: hhtm.fromLabel,
                        event: 'start_transition',
                        path: url,
                    });
                }
                // tempexp_31848_end
            },
            [onClick, dispatch, pushAction, url, hhtm, isAlternativeHHTMParamsEnabled, xsrfToken]
        );

        const externalOnClick: MouseEventHandler<HTMLButtonElement> = useCallback(
            (event) => {
                onClick && onClick(event as unknown as React.MouseEvent<HTMLAnchorElement>);
                window.open(url, target || '_blank', elementProps.rel || 'noopener');
            },
            [onClick, elementProps.rel, target, url]
        );

        if (useExternalLink) {
            if (nonMagritteExternalLink) {
                return (
                    <button
                        type="button"
                        onClick={externalOnClick}
                        ref={ref as React.ForwardedRef<HTMLButtonElement>}
                        {...(elementProps as unknown as ComponentPropsWithoutRef<'button'>)}
                    >
                        {children}
                    </button>
                );
            }

            const ExternalButton = Link as ComponentWithCustomElement<LinkProps, 'button'>;
            return (
                <ExternalButton
                    typography="label-3-regular"
                    Element="button"
                    type="button"
                    onClick={externalOnClick}
                    ref={ref as React.ForwardedRef<HTMLButtonElement>}
                    {...(elementProps as unknown as AsExternalButtonLink)}
                    inline
                >
                    {children}
                </ExternalButton>
            );
        }

        const clickHandler = useRoutingBySPA && !target && !elementProps.disabled ? internalOnClick : onClick;

        return (
            <a
                {...elementProps}
                target={target}
                href={url}
                onClick={clickHandler}
                ref={ref as React.ForwardedRef<HTMLAnchorElement>}
            >
                {children}
            </a>
        );
    }
);

SPALink.displayName = 'redux-spa-middleware/SPALink';

export default SPALink;
